/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

/***
 * This class manages models added to it. You can imagine a catalog, where are models of e.g.
 * trees, cars, building blocks etc. You can drag and drop these models to the document.
 * Then this Document can contain a house composed from the building blocks, with trees around
 * and ferrari F350 Spider in the garage.
 * @param aSceneAPI
 * @constructor
 */

import ObservableSubject from "App/observable_subject";
import BoundingBox from "App/utils/bounding_box";
import ModelStatistics from "App/model_statistics";
import FontCache from "App/utils/text/fonts/font_cache";
import Renderer from "App/renderer";
import Messenger from "App/messenger";
import Model, {EDescriptionDownloadState} from "App/model";
import {logInfo} from "App/debug";
import RenderObject from "App/geometry/objects/object";
import View from "App/view/view";
import IRenderDocument from "App/render_document_interface";
import IScene from "js_interface/src/scene_interface";
import {EEmbededElementTypes} from "App/model/elements/factory";
import IDocument from "js_interface/src/structural_elements/document_interface";
import HeadLight from "App/view/lights/head_light";
import SunLight from "App/view/lights/sun_light";

export default
class RenderDocument extends ObservableSubject implements IRenderDocument {

    public static: number = -1; /// initialized form scene api

    private sapiDocument: IDocument;
    private sceneAPI: IScene;
    private statistics: ModelStatistics;
    private renderer: Renderer;
    private fontCache: FontCache;

    private models: any = {};

    private boundingBox: BoundingBox = new BoundingBox();
    private boundingBoxForNPC: BoundingBox = new BoundingBox();
    private boundingBoxInvalid: boolean = true;

    private fpsMeasurement: any;
    private _modelStatsEventInterval: number = 0;

    // create lights here, the will be added to view in the view mode descendant classes
    protected headlight: HeadLight = null;
    protected sunlight: SunLight = null;


    constructor(aSceneAPIDocument: IDocument, aSceneAPI: IScene) {
        super();

        this.sapiDocument = aSceneAPIDocument;
        this.sceneAPI = aSceneAPI;
        this.statistics = new ModelStatistics(this);
        this.renderer = new Renderer(this.sceneAPI, this);
        this.fontCache = new FontCache(this);

        // create lights here, the will be added to view in the view mode descendant classes
        this.headlight = new HeadLight(this);
        this.sunlight = new SunLight(this);
        this.sunlight.setPositionFixed(true);

        this.fpsMeasurement = {
            lastTimeFPSWasTaken: 0,
            FPSAtT0: 0,
            framesSinceLastTime: 0,
            fps: 0
        };
    }

    public getFps(): number {
        return this.fpsMeasurement.fps;
    }

    public getFontCache() {
        return this.fontCache;
    }

    public getSceneApiDocument(): IDocument {
        return this.sapiDocument;
    }

    public release() {
        this.renderer.release();
    }

    public getStatistics(): ModelStatistics {
        return this.statistics;
    }

    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    // Edit API
    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    public addModel(aRenderModel: Model): boolean {

        if (this.models[aRenderModel.getUID()] !== undefined) {
            logInfo("Model with id " + aRenderModel.getUID() + " is already present in the render document");
            return false;
        }

        this.models[aRenderModel.getUID()] = aRenderModel;

        let descriptionDownloaded = (aModelId) => {

            let renderModel = this.models[aModelId];

            let geometryObjects = renderModel.getGeometryObjects();
            let geomObject = null;
            for (let i = 0; i < geometryObjects.length; i++) {
                geomObject = geometryObjects[i];
                this.renderer.addShape(geomObject);
            }

            if (this._checkModelDescriptionsDownloaded() && !renderModel.isInternal()) {
                Messenger.fireCDWKEvent(this.sceneAPI.getTargetTag(), "cwx3d_sceneCreated", null);
            }
        };

        let descriptionDownloadFailed = (aModelId, aReason) => {

            let renderModel = this.models[aModelId];
            if (renderModel !== undefined) {
                if (!this._checkModelDescriptionsDownloaded() && !renderModel.isInternal()) {
                    Messenger.fireCDWKEvent(this.sceneAPI.getTargetTag(), "cwx3d_sceneCreated", null);
                }
            }

            logInfo('Downloading of description for the model with id:' + aModelId + ' failed. The reason is: ' + aReason);
        };

        aRenderModel.downloadDescriptions(descriptionDownloaded, descriptionDownloadFailed);

        return true;
    }

    public removeModel(aRenderModel: Model) {
        let renderModelUID = aRenderModel.getUID();
        if (this.models[renderModelUID] !== undefined) {
            delete this.models[renderModelUID];
        }

        // stop downloading of model and geometry description
        aRenderModel.stopModelAndGeometryDescriptionDownload();

        // remove geometry objects from renderer
        let geometryObjects = aRenderModel.getGeometryObjects();
        for (let i = 0; i < geometryObjects.length; i++) {
            this.renderer.removeShape(geometryObjects[i]);
        }

        // we do not remove the geometry and model objects fom the descriptions as all of them are removed
        // so we just init the instances here
        aRenderModel.init();

        // as the shapes (geometry objects) were removed from the renderer, we need to recompute the scene bounding box
        // we do it here once instead of doing it every time the renderer.removeShape method is called
        this.invalidateBoundingBox();

        Messenger.fireCDWKEvent(this.sceneAPI.getTargetTag(), "cwx3d_modelRemoved", {
            modelId: aRenderModel.getSceneAPIModelUID()
        });

        return true;
    }

    public getSceneAPI(): IScene {
        return this.sceneAPI;
    }

    public clear() {
        let ok = true;
        for (let i in this.models) {
            if (this.models.hasOwnProperty(i)) {
                let rModel = this.models[i];
                logInfo("Model with id " + rModel.getUID() + " is being removed.");

                ok = ok && this.removeModel(rModel);
            }
        }

        return ok;
    }

    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    // Get informations API
    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    public getModelByUID(aUID: number): Model {
        return this.models[aUID];
    }

    public getMainModel(): Model {
        for (let key in this.models) {
            if (this.models[key].getSceneAPIModel().getIsMainModel()) {
                return this.models[key];
            }
        }
        return null;
    }

    public getInternalModel(): Model {
        for (let key in this.models) {
            if (this.models[key].isInternal()) {
                return this.models[key];
            }
        }
        return null;
    }

    /**
     * Return all non internal models
     * @returns {array} List of non internal render models
     */
    public getModelsList(aIncludeInternal: boolean): Model[] {
        let nonInternalModels = [];
        for (let key in this.models) {
            if (!this.models[key].isInternal() || (aIncludeInternal)) {
                nonInternalModels.push(this.models[key]);
            }
        }

        return nonInternalModels;
    }

    /**
     * Return information about all non internal models
     * @returns {Array}
     */
    public getProjectsInformationList(): any[] {
        let infos = [];
        for (let key in this.models) {
            if (!this.models[key].isInternal()) {
                infos.push(this.models[key].getProjectInfo());
            }
        }

        return infos;
    }

    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    // Rendering API
    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    public tick(aView: View) {
        this.renderer.tick(aView);
    }

    public render(aView: View) {
        this._measureFPS();

        if (this.sceneAPI.getStateViewer().isActive()) {
            this.statistics.enable();
            this.statistics.setFPS(this.fpsMeasurement.fps);
        } else {
            this.statistics.disable();
        }

        this._tryToDispatchModelStatisticsEvent();

        this.renderer.render(aView);
    }

    public renderForValues(aView: View, aProperties: any) {
        switch (aProperties.type) {
            case "cdwk_depth":
                return this.renderer.renderDepth(aView, aProperties);
            case "cdwk_geometry_object_uid":
                return this.renderer.renderGeometryObjectUID(aView, aProperties);
            case "cdwk_ground_depth":
                return this.renderer.renderDepthBellowCamera(aView, aProperties);
            case "cdwk_primitive_id":
                return this.renderer.renderPrimitiveId(aView, aProperties);
            case "cdwk_depth_custom_camera":
                return this.renderer.renderDepthCustomCamera(aView, aProperties);
            default:
                return null;
        }
    }

    public getRenderer() {
        return this.renderer;
    }

    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    // Misc API
    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

    public extendBoundingBox(aShape: RenderObject) {
        if (!aShape.affectBoundingBox()) {
            return;
        }

        this.boundingBox.extendByPoint(aShape.getBoundingBoxMin());
        this.boundingBox.extendByPoint(aShape.getBoundingBoxMax());

        this.boundingBoxForNPC.extendByPoint(aShape.getBoundingBoxMinForNPC());
        this.boundingBoxForNPC.extendByPoint(aShape.getBoundingBoxMaxForNPC());

        this.notifyObservers("onBoundingBoxUpdated", [this.boundingBox, this.boundingBoxForNPC]);
    }

    /**
     * Call this method to check whether the bbox could be smaller.
     */
    public invalidateBoundingBox() {
        this.boundingBoxInvalid = true;
    }

    public getBoundingBox() {
        if (this.boundingBoxInvalid) {
            this._reComputeBoundingBox();
        }

        return this.boundingBox.clone();
    }

    public getBoundingBoxForNPC() {
        if (this.boundingBoxInvalid) {
            this._reComputeBoundingBox();
        }

        return this.boundingBoxForNPC.clone();
    }

    public getBoundingBoxFromVisibleObjects() {
        if (this.boundingBoxInvalid) {
            this._reComputeBoundingBox();
        }

        let boundingBox = new BoundingBox();
        for (let key in this.models) {
            let model = this.models[key];
            let modelRenderBBox = model.getBoundingBoxFromVisibleObjects();
            boundingBox.extendByBBox(modelRenderBBox);
        }

        return boundingBox;
    }

    public getBoundingBoxFromVisibleObjectsForNPC() {
        if (this.boundingBoxInvalid) {
            this._reComputeBoundingBox();
        }

        let boundingBox = new BoundingBox();
        for (let key in this.models) {
            let model = this.models[key];
            let modelRenderBBox = model.getBoundingBoxFromVisibleObjectsForNPC();
            boundingBox.extendByBBox(modelRenderBBox);
        }

        return boundingBox;
    }

    public limitTexturesResolution(aValue: number) {
        this.renderer.limitTexturesResolution(aValue);
    }

    public getHeadLight(): HeadLight {
        return this.headlight;
    }

    public getSunLight(): SunLight {
        return this.sunlight;
    }

    public enableHeadLight(aValue: boolean) {
        this.headlight.enable(aValue);
    }

    public enableSunLight(aValue: boolean) {
        this.sunlight.enable(aValue);
    }

    public enableShadows(aValue: boolean) {
        // we need to recompute the projection matrix to use the projection matrix with far plane at infinite if the shadow
        // volumes are enabled, or orthographic projection if the shadow maps are enabled or normal projection matrix if
        // the shadowing is turned off at all
        let views =  this.sceneAPI.viewModeModule.getViews();
        for (let i = 0; i < views.length; i++) {
            let camera = views[i].getCamera();
            camera._invalidatePerspectiveProjMatrix();
        }
    }

    public disableShadows() {
        this.enableShadows(false);
    }

    /**
     * Set which GeoEntity types should be rendered.
     * @param aFilter An array of EEmbededElementTypes values.
     */
    public setGeoEntitiesFilter(aFilter: EEmbededElementTypes[]) {
        this.renderer.setGeoEntitiesFilter(aFilter);
    }

    private _checkModelDescriptionsDownloaded(): boolean {
        for (let i in this.models) {
            if (this.models[i].getDescriptionDownloadState() === EDescriptionDownloadState.WAITING) {
                return false;
            }
        }

        return true;
    }

    private _reComputeBoundingBox() {
        this.boundingBox.init();
        this.boundingBoxForNPC.init();

        for (let key in this.models) {
            let model = this.models[key];
            this.boundingBox.extendByBBox(model.getBoundingBox());
            this.boundingBoxForNPC.extendByBBox(model.getBoundingBoxForNPC());
        }

        this.boundingBoxInvalid = false;

        this.notifyObservers("onBoundingBoxUpdated", [this.boundingBox, this.boundingBoxForNPC]);
    }

    private _tryToDispatchModelStatisticsEvent() {
        if (this.sceneAPI.renderSettingsModule.getModelStatisticsEventDispatchingEnabled()) {
            if (!this._modelStatsEventInterval) { // setup the interval only once
                let self = this;
                this._modelStatsEventInterval = window.setInterval(function () {
                    Messenger.fireCDWKEvent(
                        self.sceneAPI.getCanvasParentTag(),
                        "cwx3d_ModelStatistics", self.statistics.getStatistics()
                    );
                }, 1000);
            }
        } else {
            if (this._modelStatsEventInterval) {
                clearInterval(this._modelStatsEventInterval);
            }
            this._modelStatsEventInterval = null;
        }
    }

    private _measureFPS() {
        let d = new Date().getTime();
        let diff = d - this.fpsMeasurement.lastTimeFPSWasTaken;

        this.fpsMeasurement.FPSAtT0 = d;

        // calc average frames per second
        if (diff >= 1000) {
            this.fpsMeasurement.fps = this.fpsMeasurement.framesSinceLastTime / (diff / 1000.0);
            this.fpsMeasurement.framesSinceLastTime = 0;
            this.fpsMeasurement.lastTimeFPSWasTaken = d;
        }
        this.fpsMeasurement.framesSinceLastTime++;

        return this.fpsMeasurement.fps;
    }

}
